// Client for the new in-app issues service (support-service backend).
// Lives separately from apiService.ts to keep the feature self-contained.

const detectStage = (): 'prod' | 'dev' | 'staging' => {
  if (typeof window === 'undefined') return 'dev';
  try {
    const v = (import.meta as any)?.env?.VITE_STAGE;
    if (v === 'prod' || v === 'staging' || v === 'dev') return v;
  } catch {
    // ignore
  }
  const h = window.location.hostname;
  if (h.startsWith('admin.dev.') || h.includes('localhost') || h.startsWith('127.')) return 'dev';
  if (h.includes('stage')) return 'staging';
  return 'prod';
};

const baseDomain = (): string => {
  if (typeof window === 'undefined') return 'cafemenumaster.com';
  const h = window.location.hostname;
  return h.replace(/^(dev|stage|www|admin\.dev|admin\.stage|admin)\./, '');
};

const issuesApiHost = (): string => {
  try {
    const fromEnv = (import.meta as any)?.env?.VITE_ISSUES_API_BASE;
    if (fromEnv) return fromEnv;
  } catch {
    // ignore
  }
  // On localhost dev, route through the Vite proxy (`/issues-api` → dev backend)
  // so we don't try to resolve `issues.api.dev.localhost`.
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  if (h === 'localhost' || h === '127.0.0.1') return '/issues-api';

  const stage = detectStage();
  const apex = baseDomain();
  if (stage === 'prod') return `https://issues.api.${apex}`;
  if (stage === 'staging') return `https://issues.api.stage.${apex}`;
  return `https://issues.api.dev.${apex}`;
};

function authHeader(): Record<string, string> {
  const token =
    (typeof localStorage !== 'undefined' && localStorage.getItem('jwtToken')) || '';
  return token ? { Authorization: token } : {};
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${issuesApiHost()}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...authHeader(),
      ...(init.headers || {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`issues API ${res.status}: ${text || res.statusText}`);
  }
  return res.json() as Promise<T>;
}

/**
 * The assignment half of an issue update. `assigneeEmail` is always present:
 * a string when it is known, `null` when it is not.
 */
export interface IssueAssignmentPatch {
  assigneeId: string;
  assigneeEmail: string | null;
}

/**
 * Build an assignment patch from whatever was typed into the assignee field.
 *
 * THE KEY IS ALWAYS THERE. Omitting `assigneeEmail` when the email is unknown
 * would leave the previous assignee's email on the row -- and since the service
 * matches prod issues to testers BY EMAIL, that previous person would keep
 * seeing the new assignee's issues. `null` says "nobody"; absent says
 * "unchanged", and they are not the same claim.
 *
 * There is no admin directory in this app to resolve a sub to an address, so
 * the email is known exactly when what was typed IS one. Anything else --
 * a raw sub, a name, an empty box -- is honestly unknown.
 */
export const buildAssignmentPatch = (raw: string): IssueAssignmentPatch => {
  const value = (raw || '').trim();
  const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  return { assigneeId: value, assigneeEmail: isEmail ? value.toLowerCase() : null };
};

export interface AdminListFilters {
  /**
   * `awaiting_owner_approval` (2026-09-26) is the issue-fix platform's status: a
   * tester has signed a fix off and it is waiting on the owner. ADDITIVE -- the
   * list endpoint already accepted it and this union was simply lagging the
   * service, which is why `SuperAdminAppIssuesQueue` had to cast to reach it.
   * Nothing that filters on the older six changes.
   */
  status?: 'new' | 'in_progress' | 'deploying' | 'awaiting_retest'
  | 'awaiting_owner_approval' | 'resolved' | 'rejected';
  priority?: 'minor' | 'annoying' | 'blocking' | 'critical';
  tenantId?: string;
  /** Device-health auto tickets (2026-10-04): only this category, e.g. 'device_auto'. */
  category?: string;
  /** Everything except this category — the "Reported by people" pill. */
  notCategory?: string;
  limit?: number;
  cursor?: string;
}

// Unified support center (2026-08-26) — a single AppIssue can now represent an
// app bug, a device problem, a billing question, or a guided "question", and
// carries who/what it's about. See packages/shared-types
// src/schemas/entities/app-issue.schema.ts for the backend source of truth.
export type TicketKind = 'issue' | 'device' | 'billing' | 'question' | 'contact';

export interface TicketContact {
  name: string;
  email: string;
  phone?: string;
  preferredMethod?: 'email' | 'phone';
}

export interface TicketDeviceRef {
  unitId?: string;
  serialNumber?: string;
  modelId?: string;
  modelName?: string;
  manufacturer?: string;
  terminalId?: string;
  registerName?: string;
  locationId?: string;
  locationName?: string;
  terminalType?: string;
}

export type AppIssueSource = 'web' | 'pos-terminal' | 'public' | 'pos-auto';
export type IssueCommentKind = 'comment' | 'status_change' | 'phone_call';

export interface AdminIssue {
  id: string;
  tenantId: string;
  reporterId: string;
  reporterEmail: string;
  reporterName?: string;
  title: string;
  description: string;
  category: string;
  priority: 'minor' | 'annoying' | 'blocking' | 'critical';
  status: 'new' | 'in_progress' | 'deploying' | 'awaiting_retest' | 'resolved' | 'rejected';
  kind?: TicketKind;
  source?: AppIssueSource;
  contact?: TicketContact;
  device?: TicketDeviceRef;
  context?: any;
  assigneeId?: string;
  /**
   * The assignee's email, alongside their `assigneeId` (a Cognito `sub`).
   *
   * BOTH ARE NEEDED BECAUSE A SUB IS PER-POOL. Dev and prod have separate admin
   * pools, so the same human has a different `sub` on each stage and a prod
   * row's `assigneeId` can never match a dev caller's. The service matches prod
   * rows on email and dev rows on sub, which only works if this is written.
   *
   * `null` means assigned to somebody whose email is not known -- which is a
   * fact, and a different fact from "unchanged".
   */
  assigneeEmail?: string | null;
  resolutionNote?: string;
  createdAt: number;
  updatedAt: number;
  [key: string]: any;
}

export interface AdminIssueComment {
  id: string;
  issueId: string;
  authorId: string;
  authorEmail: string;
  authorName?: string;
  authorRole: 'reporter' | 'super_admin' | 'system';
  body: string;
  internal: boolean;
  kind?: IssueCommentKind;
  createdAt: number;
}

export const issuesApi = {
  list: (filters: AdminListFilters = {}) => {
    const qs = new URLSearchParams();
    if (filters.status) qs.set('status', filters.status);
    if (filters.priority) qs.set('priority', filters.priority);
    if (filters.tenantId) qs.set('tenantId', filters.tenantId);
    if (filters.category) qs.set('category', filters.category);
    if (filters.notCategory) qs.set('notCategory', filters.notCategory);
    if (filters.limit) qs.set('limit', String(filters.limit));
    if (filters.cursor) qs.set('cursor', filters.cursor);
    const q = qs.toString();
    return call<{
      issues: AdminIssue[];
      count: number;
      counts: Record<string, number>;
      /** Per-category counts before the category filter (support-service, 2026-10-04). */
      categoryCounts?: Record<string, number>;
      /**
       * The DEV table's continuation token. It is a DynamoDB key from ONE
       * specific table, so it cannot describe a position in two -- which is
       * why the prod side reports its own truncation separately below.
       */
      nextCursor?: string;
      /**
       * The prod side came back as one unpaginated page and there may be more
       * of it. Declared here because the service has always sent it
       * (`admin.listIssues`) and a caller that cannot read it can only either
       * claim completeness it does not have, or say nothing -- and silence
       * about a truncated list is the failure this whole field exists to stop.
       */
      prodMayHaveMore?: boolean;
    }>(
      `/admin/issues${q ? `?${q}` : ''}`,
    );
  },

  delete: (id: string) =>
    call<{ deleted: boolean; id: string }>(`/admin/issues/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  get: (id: string) =>
    call<{ issue: AdminIssue; comments: AdminIssueComment[] }>(`/admin/issues/${encodeURIComponent(id)}`),

  update: (id: string, body: {
    status?: 'new' | 'in_progress' | 'deploying' | 'awaiting_retest' | 'resolved' | 'rejected';
    priority?: 'minor' | 'annoying' | 'blocking' | 'critical';
    assigneeId?: string;
    /**
     * Sent as `null`, NEVER omitted, when the email is unknown -- JSON drops an
     * omitted key and the service's merge then keeps the stale value, which
     * means the PREVIOUS assignee goes on matching and sees the new assignee's
     * issues. Use `buildAssignmentPatch` rather than assembling this by hand.
     */
    assigneeEmail?: string | null;
    resolutionNote?: string;
  }) =>
    call<{ issue: AdminIssue }>(`/admin/issues/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  comment: (id: string, body: string, opts: { internal?: boolean; kind?: 'comment' | 'phone_call' } = {}) =>
    call<{ comment: AdminIssueComment }>(`/admin/issues/${encodeURIComponent(id)}/comments`, {
      method: 'POST',
      body: JSON.stringify({ body, internal: opts.internal ?? false, ...(opts.kind ? { kind: opts.kind } : {}) }),
    }),

  getAiPrompt: (id: string) =>
    call<{ prompt: string; screenshotUrl?: string; issueId: string }>(
      `/admin/issues/${encodeURIComponent(id)}/ai-prompt`,
    ),

  getScreenshotUrl: (id: string) =>
    call<{ url: string | null }>(`/admin/issues/${encodeURIComponent(id)}/screenshot-url`),
};
